import React, { useState, useEffect } from "react";
import {
  Box,
  Tooltip,
  Typography,
  Chip,
  CircularProgress,
  makeStyles,
  useTheme
} from "@material-ui/core";
import {
  Wifi,
  WifiOff,
  SignalWifi4Bar,
  SignalWifiOff,
  Error,
  CheckCircle,
  RadioButtonChecked,
  RadioButtonUnchecked
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5)
  },
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    transition: "all 0.3s ease",
    position: "relative",
    "&:before": {
      content: '""',
      position: "absolute",
      top: -2,
      left: -2,
      right: -2,
      bottom: -2,
      borderRadius: "50%",
      opacity: 0
    }
  },
  connected: {
    backgroundColor: theme.palette.success.main,
    "&:before": {
      backgroundColor: theme.palette.success.main,
      animation: "$pulse 2s infinite"
    }
  },
  connecting: {
    backgroundColor: theme.palette.warning.main
  },
  disconnected: {
    backgroundColor: theme.palette.grey[400]
  },
  error: {
    backgroundColor: theme.palette.error.main,
    "&:before": {
      backgroundColor: theme.palette.error.main,
      animation: "$pulse 2s infinite"
    }
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 0.7
    },
    "50%": {
      transform: "scale(1.3)",
      opacity: 0.3
    },
    "100%": {
      transform: "scale(1)",
      opacity: 0
    }
  },
  statusText: {
    fontSize: 12,
    fontWeight: 500
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    fontSize: 16
  }
}));

const ConnectionStatusIndicator = ({
  status = "disconnected",
  isActive = false,
  showText = false,
  showIcon = true,
  compact = false
}) => {
  const classes = useStyles();
  const theme = useTheme();
  
  const [signalStrength, setSignalStrength] = useState(4);

  // Simulate signal strength changes
  useEffect(() => {
    if (status === "connected" && isActive) {
      const interval = setInterval(() => {
        setSignalStrength(prev => {
          const change = Math.random() > 0.7 ? (Math.random() > 0.5 ? 1 : -1) : 0;
          return Math.max(1, Math.min(4, prev + change));
        });
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [status, isActive]);

  // Get status configuration
  const getStatusConfig = () => {
    switch (status) {
      case "connected":
        return {
          color: theme.palette.success.main,
          icon: signalStrength > 2 ? <SignalWifi4Bar /> : <Wifi />,
          text: i18n.t("voiceAssistant.connectionStatus.connected"),
          className: classes.connected
        };
      case "connecting":
        return {
          color: theme.palette.warning.main,
          icon: <CircularProgress size={16} />,
          text: i18n.t("voiceAssistant.connectionStatus.connecting"),
          className: classes.connecting
        };
      case "error":
        return {
          color: theme.palette.error.main,
          icon: <Error />,
          text: i18n.t("voiceAssistant.connectionStatus.error"),
          className: classes.error
        };
      case "timeout":
        return {
          color: theme.palette.error.main,
          icon: <Error />,
          text: i18n.t("voiceAssistant.connectionStatus.timeout"),
          className: classes.error
        };
      case "networkError":
        return {
          color: theme.palette.error.main,
          icon: <SignalWifiOff />,
          text: i18n.t("voiceAssistant.connectionStatus.networkError"),
          className: classes.error
        };
      default:
        return {
          color: theme.palette.grey[400],
          icon: <WifiOff />,
          text: i18n.t("voiceAssistant.connectionStatus.disconnected"),
          className: classes.disconnected
        };
    }
  };

  const config = getStatusConfig();

  // Get detailed tooltip content
  const getTooltipContent = () => {
    let content = config.text;
    
    if (status === "connected" && isActive) {
      content += `\nForça do sinal: ${signalStrength}/4`;
      content += `\nSessão: ${isActive ? "Ativa" : "Inativa"}`;
    }
    
    return content;
  };

  if (compact) {
    return (
      <Tooltip title={getTooltipContent()} arrow>
        <Box className={classes.root}>
          <Box className={`${classes.statusIndicator} ${config.className}`} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={getTooltipContent()} arrow>
      <Box className={classes.root}>
        {showIcon && (
          <Box className={classes.iconContainer} style={{ color: config.color }}>
            {config.icon}
          </Box>
        )}
        
        <Box className={`${classes.statusIndicator} ${config.className}`} />
        
        {showText && (
          <Typography className={classes.statusText} style={{ color: config.color }}>
            {config.text}
          </Typography>
        )}
        
        {isActive && status === "connected" && (
          <Box ml={0.5}>
            <RadioButtonChecked
              style={{ 
                color: theme.palette.success.main,
                fontSize: 12
              }}
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default ConnectionStatusIndicator;